import { useState } from "react";
import type { MeetingDTO } from "../../shared/types";
import { END_WORDS, peopleWords, signalWords, transcriptionWords } from "../lib/meetings";
import { TextLink } from "./TextLink";

const clock = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hourCycle: "h23" });

/**
 * How a meeting ended (AIT-364): why the bot left, how many people it saw at most and over time, whether Meet's speaking
 * marks and the captions' names appeared (AIT-370), and why nothing was captured. None of it is the meeting's content, so an admin who is not in it reads it too. What the page showed when
 * the call went away (its words and a screenshot) is content: it is on the row only for the meeting's members, and
 * `fileHref` opens the screenshot.
 */
export function MeetingEnd({ meeting, fileHref }: { meeting: MeetingDTO; fileHref?: (fileId: string) => string }) {
  const [showPage, setShowPage] = useState(false);
  const signals = signalWords(meeting);
  const transcribed = transcriptionWords(meeting);
  if (!meeting.endReason && !meeting.nothingCaptured && !signals && !transcribed) return null;
  const people = peopleWords(meeting);
  const track = meeting.peopleSeen.track;
  return (
    <div className="space-y-1 text-xs text-muted">
      {meeting.endReason && (
        <p>
          <b className="font-medium text-fg">Pandas left:</b> {meeting.endDetail ?? END_WORDS[meeting.endReason]}
          {people && <> · {people}</>}
        </p>
      )}
      {track.length > 1 && (
        <p className="break-words">
          <b className="font-medium text-fg">People over time:</b> {track.map((t) => `${clock(t.at)} ${t.n}`).join(" · ")}
        </p>
      )}
      {signals && (
        <p className={signals.missing ? "text-warning" : undefined}>
          <b className="font-medium text-fg">Who was speaking:</b> {signals.words}
        </p>
      )}
      {transcribed && (
        <p className={meeting.transcription?.status === "failed" ? "text-warning" : undefined}>
          <b className="font-medium text-fg">Transcript:</b> {transcribed}
        </p>
      )}
      {meeting.nothingCaptured && (
        <p>
          <b className="font-medium text-fg">Nothing captured:</b> {meeting.nothingCaptured}
        </p>
      )}
      {(meeting.endPageText || (meeting.debugFileId && fileHref)) && (
        <div className="space-y-1">
          <p className="flex flex-wrap gap-x-3">
            {meeting.endPageText && <TextLink onClick={() => setShowPage(!showPage)}>{showPage ? "Hide what the page showed" : "What the page showed"}</TextLink>}
            {meeting.debugFileId && fileHref && <TextLink href={fileHref(meeting.debugFileId)} newTab>Screenshot</TextLink>}
          </p>
          {showPage && meeting.endPageText && <p className="rounded-md bg-fill-subtle p-3 break-words whitespace-pre-wrap">{meeting.endPageText}</p>}
        </div>
      )}
    </div>
  );
}
